<template>
  <div>
    <div class="nk-app-root">
      <div class="nk-main">
        <div class="nk-wrap">
          <Header v-show="!loading_page" />
          <div class="nk-content">
            <div class="container wide-xl">
              <div class="nk-content-inner">
                <Navigation v-show="!loading_page" />
                <div class="nk-content-body" v-show="!loading_page">
                  <div class="nk-content-wrap">
                    <div class="nk-block nk-block-lg">
                      <div class="nk-block-head">
                        <div class="nk-block-head-content">
                          <h4 class="nk-block-title">{{ $t('dm_ai_usage_title') }}</h4>
                        </div>
                      </div>

                      <!-- Summary -->
                      <div class="dm-usage-stats-row" v-if="summary">
                        <div class="dm-stat">
                          <span class="dm-stat-num">{{ summary.total_requests }}</span>
                          <span class="dm-stat-lbl">{{ $t('dm_ai_usage_requests') }}</span>
                        </div>
                        <div class="dm-stat">
                          <span class="dm-stat-num">{{ formatK(summary.total_prompt_tokens) }}</span>
                          <span class="dm-stat-lbl">Prompt</span>
                        </div>
                        <div class="dm-stat">
                          <span class="dm-stat-num">{{ formatK(summary.total_completion_tokens) }}</span>
                          <span class="dm-stat-lbl">Completion</span>
                        </div>
                        <div class="dm-stat dm-stat-accent">
                          <span class="dm-stat-num">{{ formatK(summary.total_tokens) }}</span>
                          <span class="dm-stat-lbl">Total</span>
                        </div>
                      </div>

                      <!-- Filters -->
                      <div class="dm-usage-filters">
                        <select class="form-control form-control-sm" v-model="filterEndpoint" @change="loadUsage">
                          <option value="">Alle Endpoints</option>
                          <option value="kalkulator/ai-parse">Parse</option>
                          <option value="preisliste/ai-chat">Chat</option>
                        </select>
                        <input type="date" class="form-control form-control-sm" v-model="filterVon" @change="loadUsage" />
                        <input type="date" class="form-control form-control-sm" v-model="filterBis" @change="loadUsage" />
                        <input type="text" class="form-control form-control-sm" style="width: 140px;" :placeholder="$t('dm_ai_usage_filter_user')" v-model="filterBenutzer" @keyup.enter="loadUsage" />
                      </div>

                      <!-- Table -->
                      <div class="card card-bordered">
                        <div class="dm-table-wrap" style="max-height: calc(100vh - 420px);">
                          <table class="dm-table dm-table-compact">
                            <thead>
                              <tr>
                                <th>{{ $t('dm_ai_usage_col_time') }}</th>
                                <th>{{ $t('dm_ai_usage_col_user') }}</th>
                                <th>Endpoint</th>
                                <th>{{ $t('dm_ai_usage_col_model') }}</th>
                                <th class="text-right">Prompt</th>
                                <th class="text-right">Completion</th>
                                <th class="text-right">Total</th>
                              </tr>
                            </thead>
                            <tbody>
                              <tr v-for="(item, idx) in list" :key="idx">
                                <td class="text-muted small">{{ formatDate(item.zeitstempel) }}</td>
                                <td>{{ item.benutzer_name || '—' }}</td>
                                <td>
                                  <span class="dm-endpoint-badge" :class="item.endpoint === 'kalkulator/ai-parse' ? 'dm-ep-parse' : 'dm-ep-chat'">
                                    {{ item.endpoint === 'kalkulator/ai-parse' ? 'Parse' : 'Chat' }}
                                  </span>
                                </td>
                                <td class="text-muted small">{{ shortModel(item.model) }}</td>
                                <td class="text-right">{{ (item.prompt_tokens || 0).toLocaleString('de-DE') }}</td>
                                <td class="text-right">{{ (item.completion_tokens || 0).toLocaleString('de-DE') }}</td>
                                <td class="text-right fw-bold">{{ (item.total_tokens || 0).toLocaleString('de-DE') }}</td>
                              </tr>
                              <tr v-if="list.length === 0 && !loading">
                                <td colspan="7" class="text-center text-muted py-4">{{ $t('dm_common_no_entries_found') }}</td>
                              </tr>
                            </tbody>
                          </table>
                        </div>
                      </div>

                      <!-- Pagination -->
                      <div class="d-flex justify-content-between align-items-center mt-3" v-if="total > pageSize">
                        <span class="text-muted small">{{ total }} {{ $t('dm_ai_usage_entries') }}</span>
                        <ul class="pagination pagination-sm">
                          <li class="page-item" :class="{ disabled: page === 1 }">
                            <a class="page-link" href="#" @click.prevent="goPage(page - 1)">&laquo;</a>
                          </li>
                          <li class="page-item" v-for="p in pageCount" :key="p" :class="{ active: page === p }">
                            <a class="page-link" href="#" @click.prevent="goPage(p)">{{ p }}</a>
                          </li>
                          <li class="page-item" :class="{ disabled: page === pageCount }">
                            <a class="page-link" href="#" @click.prevent="goPage(page + 1)">&raquo;</a>
                          </li>
                        </ul>
                      </div>

                    </div>
                  </div>
                  <Footer />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Header from "../../../components/Header.vue";
import Navigation from "../../../components/Navigation.vue";
import Footer from "../../../components/Footer.vue";
import dmPermissionsMixin from "@/mixins/dm-permissions-mixin";
import { getAiUsage } from "@/services/dm.service";

export default {
  name: "DmAiUsage",
  components: { Header, Navigation, Footer },
  mixins: [dmPermissionsMixin],
  data() {
    return {
      list: [],
      total: 0,
      summary: null,
      loading: false,
      page: 1,
      pageSize: 50,
      filterEndpoint: "",
      filterVon: "",
      filterBis: "",
      filterBenutzer: "",
    };
  },
  computed: {
    loading_page() {
      return this.$store.state.loading_page && this.$store.state.loading_page === "setup";
    },
    pageCount() {
      return Math.max(1, Math.ceil(this.total / this.pageSize));
    },
  },
  created() {
    this.loadUsage();
  },
  methods: {
    loadUsage: function() {
      var self = this;
      self.loading = true;
      var filters = {
        limit: self.pageSize,
        skip: (self.page - 1) * self.pageSize,
      };
      if (self.filterEndpoint) filters.endpoint = self.filterEndpoint;
      if (self.filterVon) filters.von = self.filterVon;
      if (self.filterBis) filters.bis = self.filterBis;
      if (self.filterBenutzer) filters.benutzer_id = self.filterBenutzer;

      getAiUsage(filters)
        .then(function(d) {
          self.loading = false;
          if (d && d.data) {
            self.list = d.data.list || [];
            self.total = d.data.total || 0;
            self.summary = d.data.summary || null;
          }
        })
        .catch(function(e) {
          self.loading = false;
          self.$store.dispatch("api_error", e);
        });
    },
    goPage: function(p) {
      if (p < 1 || p > this.pageCount) return;
      this.page = p;
      this.loadUsage();
    },
    formatDate: function(d) {
      if (!d) return "—";
      var date = new Date(d);
      return date.toLocaleDateString("de-DE", { day: "2-digit", month: "2-digit", year: "2-digit" })
        + " " + date.toLocaleTimeString("de-DE", { hour: "2-digit", minute: "2-digit" });
    },
    formatK: function(n) {
      if (n >= 1000000) return (n / 1000000).toFixed(1).replace(".", ",") + "M";
      if (n >= 10000) return (n / 1000).toFixed(1).replace(".", ",") + "k";
      return n.toLocaleString("de-DE");
    },
    shortModel: function(m) {
      if (!m) return "—";
      return m.replace("anthropic/", "").replace("claude-", "");
    },
  },
};
</script>

<style scoped>
/* Stats row */
.dm-usage-stats-row {
  display: flex;
  gap: 4px;
  margin-bottom: 16px;
}

.dm-stat {
  flex: 1;
  padding: 14px 16px;
  background: #fff;
  border: 1px solid rgba(128, 128, 128, 0.12);
  border-radius: 8px;
  text-align: center;
}

.dm-stat-accent {
  border-color: rgba(232, 117, 26, 0.25);
  background: rgba(232, 117, 26, 0.03);
}

.dm-stat-num {
  font-size: 20px;
  font-weight: 700;
  color: #333;
  display: block;
  line-height: 1.2;
}

.dm-stat-accent .dm-stat-num {
  color: #E8751A;
}

.dm-stat-lbl {
  font-size: 10px;
  text-transform: uppercase;
  letter-spacing: 0.4px;
  color: #aaa;
  margin-top: 2px;
  display: block;
}

/* Filters */
.dm-usage-filters {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 16px;
}

.dm-usage-filters .form-control-sm {
  width: auto;
}

.dm-table-wrap {
  overflow: auto;
}

.dm-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;
}

.dm-table-compact td,
.dm-table-compact th {
  padding: 8px 12px;
}

.dm-table thead {
  background: rgba(128, 128, 128, 0.06);
  position: sticky;
  top: 0;
  z-index: 1;
}

.dm-table thead th {
  font-weight: 600;
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: #888;
  border-bottom: 2px solid rgba(128, 128, 128, 0.15);
}

.dm-table tbody td {
  border-bottom: 1px solid rgba(128, 128, 128, 0.08);
}

.dm-table tbody tr:nth-child(even) {
  background: rgba(128, 128, 128, 0.03);
}

.dm-table tbody tr:hover {
  background: rgba(128, 128, 128, 0.06);
}

.text-right {
  text-align: right;
}

.fw-bold {
  font-weight: 600;
}

.dm-endpoint-badge {
  display: inline-block;
  padding: 2px 8px;
  border-radius: 4px;
  font-size: 11px;
  font-weight: 600;
}

.dm-ep-parse {
  background: rgba(59, 130, 246, 0.1);
  color: #2563eb;
}

.dm-ep-chat {
  background: rgba(232, 117, 26, 0.1);
  color: #c5600e;
}
</style>
